import { findAiTaskRanges, type EditorInstance } from "novel";
import { DOMParser as PmDOMParser } from "@tiptap/pm/model";

/** 去掉模型偶发包裹的 ```markdown ... ``` */
export function unwrapMarkdownFence(md: string) {
  const t = md.trim();
  const m = t.match(/^```(?:markdown|md)?\s*\n([\s\S]*?)\n```$/i);
  return (m?.[1] ?? t).trim();
}

function looksLikeBlockMarkdown(md: string) {
  return /^(#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```)/m.test(md) || /\n\s*\n/.test(md);
}

/**
 * 把区间内字面量 Markdown 解析成标题/列表等块级节点并替换。
 * 流式阶段仍可插纯文本；在「应用」或选区生成结束时调用。
 * 块级 MD 扩到整段；行内/单段只替换精确区间，避免吃掉选区外文字。
 */
export function applyMarkdownInRange(
  editor: EditorInstance,
  from: number,
  to: number,
): boolean {
  if (from >= to) return false;
  const md = unwrapMarkdownFence(
    editor.state.doc.textBetween(from, to, "\n\n", "\n"),
  );
  if (!md) return false;

  const parser = (
    editor.storage as {
      markdown?: { parser?: { parse: (c: string) => string } };
    }
  ).markdown?.parser;
  if (!parser) return false;

  const html = parser.parse(md);
  const wrap = document.createElement("div");
  wrap.innerHTML = html;
  const pmNode = PmDOMParser.fromSchema(editor.schema).parse(wrap);
  if (!pmNode.content.size) return false;

  let replaceFrom = from;
  let replaceTo = to;
  if (looksLikeBlockMarkdown(md)) {
    const $from = editor.state.doc.resolve(from);
    const $to = editor.state.doc.resolve(Math.max(from, to - 1));
    const depth = Math.min($from.depth, $to.depth);
    if (depth >= 1) {
      replaceFrom = $from.before(depth);
      replaceTo = $to.after(depth);
    }
  }

  return editor
    .chain()
    .focus()
    .command(({ tr, dispatch }) => {
      if (dispatch) {
        tr.replaceWith(replaceFrom, replaceTo, pmNode.content);
        dispatch(tr);
      }
      return true;
    })
    .run();
}

/** 卸掉指定 task 残留的 aiMark（应用成功后应无残留；失败时清蓝字） */
export function clearAiMarksForTask(editor: EditorInstance, taskId: string) {
  const left = findAiTaskRanges(editor, taskId);
  if (!left) return;
  editor
    .chain()
    .focus()
    .setTextSelection({ from: left.from, to: left.to })
    .unsetAiMark()
    .run();
}

/** 打开稿时：从文档里扫出未完成的 aiMark，恢复浮层任务 */
export function recoverAiTasksFromDoc(
  editor: EditorInstance,
  addTask: (task: {
    id: string;
    status: "generated";
    wordCount: number;
  }) => void,
  knownIds: Set<string>,
) {
  const counts = new Map<string, number>();
  editor.state.doc.descendants((node) => {
    if (!node.isText || !node.marks.length) return;
    const mark = node.marks.find((m) => m.type.name === "aiMark");
    const id = mark?.attrs?.taskId as string | undefined;
    if (!id || knownIds.has(id)) return;
    counts.set(id, (counts.get(id) || 0) + (node.text?.length || 0));
  });
  for (const [id, chars] of counts) {
    addTask({ id, status: "generated", wordCount: chars });
    knownIds.add(id);
  }
}
